import { useState, useEffect } from "react";
import styled from "styled-components";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import "dayjs/locale/es";
import { ChallengeScheme, UserScheme } from "client-v2";
import ReliefModal from "../modal/ReliefModal";
import ResumeStyled from "../container/ResumeContainer";
import {
	Text,
	Color,
	Button,
	ProgressBar,
	ColorV2,
	Menu,
} from "@adoptaunabuelo/react-components";
import {
	Cake,
	Calendar,
	Facebook,
	Flower,
	Link,
	MessageCircle,
	PartyPopper,
	PenLine,
	Rocket,
	Share,
	Twitter,
	Users2,
} from "lucide-react";
import { State } from "../../redux";
import { useSelector } from "react-redux";

const Section = styled.div``;
const Row = styled.div`
	display: flex;
	flex-direction: row;
`;
const Col = styled.div`
	display: flex;
	flex-direction: column;
`;
const SpanLink = styled.span`
	cursor: pointer;
	text-decoration: underline;
`;
const Icon = styled.div`
	display: flex;
	align-items: center;
	justify-content: center;
	height: 44px;
	width: 44px;
	border-radius: 8px;
	background-color: ${ColorV2.surface.background};
`;
const Chip = styled.div`
	display: flex;
	padding: 6px 14px;
	border-radius: 20px;
	border: 1px solid ${ColorV2.border.neutralSoft};
	width: fit-content;
	align-items: center;
	justify-content: center;
	gap: 6px;
`;

const Resume = (props: Props) => {
	dayjs.extend(relativeTime);
	dayjs.locale("es");
	const appLocation = useSelector((state: State) => state.location.location);
	const [showReliefModal, setShowReliefModal] = useState(false);
	const [pastDate, setPastDate] = useState(false);
	const [completed, setCompleted] = useState(false);

	useEffect(() => {
		//Check if past date
		const diff = dayjs(props.challenge.endDate.iso).diff(dayjs(), "days");
		if (diff <= 0) setPastDate(true);

		//Check if goal completed
		if (props.challenge.type === "birthday") {
			setCompleted(false);
		} else {
			const restAmount = props.challenge.goal - props.challenge.amount;
			if (restAmount <= 0) setCompleted(true);
		}
	}, [props.challenge]);

	const calculateAmount = (amount: number): string => {
		const tempAmount =
			amount / (appLocation ? appLocation.currency.conversion : 1);
		const tempCurrencyName = appLocation
			? appLocation.currency.name
			: "eur";
		const tempLanguage = appLocation ? appLocation.language : "es-ES";

		return new Intl.NumberFormat(tempLanguage, {
			style: "currency",
			currency: tempCurrencyName,
			maximumFractionDigits: 0,
		}).format(tempAmount);
	};

	const renderCell = (data: {
		icon: React.ComponentType<any>;
		iconColor?: string;
		iconBackground?: string;
		title: string;
		subtitle?: string;
		style?: {
			margin?: number;
			fontSize?: number;
			textColor?: string;
			fontWeight?: number;
		};
	}) => (
		<Row
			style={{
				gap: 12,
				marginBottom: data.style?.margin ? data.style.margin : 0,
				justifyContent: data.subtitle ? "flex-start" : "center",
			}}
		>
			<Icon
				style={{
					backgroundColor: data.iconBackground
						? data.iconBackground
						: ColorV2.surface.background,
				}}
			>
				<data.icon
					style={{
						minHeight: 24,
						minWidth: 24,
					}}
					height={24}
					width={24}
					color={
						data.iconColor
							? data.iconColor
							: ColorV2.text.neutralHard
					}
				/>
			</Icon>
			<Col
				style={{
					flex: 1,
				}}
			>
				<Text
					type="p"
					weight="medium"
					style={{
						fontSize: data.style?.fontSize,
						fontWeight: data.style?.fontWeight,
						color: data.style?.textColor,
					}}
				>
					{data.title}
				</Text>
				{data.subtitle && (
					<Text
						type="p"
						style={{
							color: ColorV2.text.neutralMedium,
						}}
					>
						{data.subtitle}
					</Text>
				)}
			</Col>
		</Row>
	);

	return (
		<ResumeStyled>
			<ReliefModal
				isVisible={showReliefModal}
				price={50}
				charge={"one"}
				onClose={() => setShowReliefModal(false)}
			/>
			<Section>
				<Row
					style={{
						justifyContent: "space-between",
						alignItems: "center",
						marginBottom: 16,
					}}
				>
					<Chip>
						{props.challenge.type === "signature" ? (
							<PenLine
								height={20}
								width={20}
								color={ColorV2.text.neutralHard}
							/>
						) : props.challenge.type === "birthday" ? (
							<Cake
								height={20}
								width={20}
								color={ColorV2.text.neutralHard}
							/>
						) : (
							<Rocket
								height={20}
								width={20}
								color={ColorV2.text.neutralHard}
							/>
						)}
						<Text type="p2">
							{props.challenge.type === "signature"
								? "Firma"
								: props.challenge.type === "birthday"
								? "Cumpleaños"
								: "Sueño"}
						</Text>
					</Chip>
					<Menu
						id={"share"}
						position={"bottom-left"}
						menuStyle={{ width: 200, top: 32, right: 0 }}
						options={[
							{
								id: "twitter",
								label: "Twitter",
								icon: <Twitter height={20} width={20} />,
							},
							{
								id: "facebook",
								label: "Facebook",
								icon: <Facebook height={20} width={20} />,
							},
							{
								id: "whatsapp",
								label: "Whatsapp",
								icon: <MessageCircle height={20} width={20} />,
							},
							{
								id: "url",
								label: "Copiar enlace",
								icon: <Link height={20} width={20} />,
							},
						]}
						Icon={
							<div
								style={{
									display: "flex",
									alignItems: "center",
									gap: 8,
								}}
							>
								<Share width={20} height={20} />
								<Text type="b2">Compartir</Text>
							</div>
						}
						onClick={props.onShareClick}
					/>
				</Row>
				<Text type="h3" weight="semibold" style={{ marginBottom: 16 }}>
					{props.challenge.title}
				</Text>
				{props.challenge.to && props.challenge.type === "signature" && (
					<Col style={{ marginBottom: 24 }}>
						<Text
							type="p2"
							style={{ color: ColorV2.text.neutralMedium }}
						>
							Dirigido a:
						</Text>
						<Text type="p" weight="medium">
							{props.challenge.to.title}
						</Text>
					</Col>
				)}
				{props.challenge.type === "birthday" ? (
					renderCell({
						icon: PartyPopper,
						iconColor: ColorV2.text.primary,
						iconBackground: ColorV2.border.primarySoft,
						title: props.letterCount + " felicitaciones",
						style: {
							margin: 12,
							fontSize: 28,
							fontWeight: 600,
							textColor: ColorV2.text.primary,
						},
					})
				) : (
					<>
						<ProgressBar
							style={{ height: 8, marginBottom: 8 }}
							progress={[
								{
									value:
										(props.challenge.amount /
											props.challenge.goal) *
										100,
									color: completed
										? Color.status.color.success
										: props.challenge.type === "rose"
										? ColorV2.surface.redMedium
										: Color.background.primary,
								},
							]}
							animationTime={0.3}
							animationDelay={0.5}
						/>
						<Row style={{ alignItems: "center", marginBottom: 24 }}>
							<Text
								type="h3"
								weight={"medium"}
								style={{
									color: completed
										? Color.status.color.success
										: props.challenge.type === "rose"
										? ColorV2.surface.redMedium
										: Color.text.primary,
								}}
							>
								{props.challenge.type === "signature"
									? props.challenge.amount.toLocaleString()
									: calculateAmount(props.challenge.amount)}
							</Text>
							<Text
								type="p"
								style={{
									marginLeft: 8,
									marginTop: 4,
									color: ColorV2.text.neutralMedium,
								}}
							>
								de{" "}
								{props.challenge.type === "signature"
									? props.challenge.goal.toLocaleString() +
									  " firmas"
									: calculateAmount(props.challenge.goal)}
							</Text>
						</Row>
					</>
				)}
				<Col style={{ gap: 16 }}>
					{props.challenge.type !== "birthday" &&
						completed &&
						renderCell({
							icon: PartyPopper,
							iconColor: ColorV2.text.green,
							iconBackground: ColorV2.surface.greenSoft,
							title: "¡Conseguido!",
							subtitle:
								props.challenge.type === "dream"
									? "Puedes seguir colaborando y ayudarnos a cumplir los sueños de otros abuelos."
									: props.challenge.type === "signature"
									? "Hemos llegado al objetivo, pero puedes seguir firmando y ayudando."
									: "",
						})}
					{props.challenge.type === "signature" &&
						renderCell({
							icon: PenLine,
							title: "Última firma",
							subtitle:
								props.supporters.length > 0
									? (
											dayjs(
												props.supporters[0].createdAt
											) as any
									  ).fromNow()
									: "",
						})}
					{props.challenge.type === "rose" ? (
						<Row>
							<Users2
								style={{
									marginRight: 12,
									minHeight: 24,
									minWidth: 24,
								}}
								height={24}
								width={24}
								color={ColorV2.text.neutralHard}
							/>
							<Text
								type="p"
								style={{
									color: ColorV2.text.neutralMedium,
									marginTop: 1,
								}}
							>
								<span
									style={{
										fontWeight: 600,
										color: ColorV2.text.neutralHard,
									}}
								>
									{props.challenge.supporters.toLocaleString() +
										" personas "}
								</span>
								han colaborado enviando una rosa
							</Text>
						</Row>
					) : (
						props.challenge.type !== "signature" &&
						props.challenge.type !== "birthday" &&
						renderCell({
							icon: Users2,
							title:
								props.challenge.supporters.toLocaleString() +
								" personas han colaborado",
							subtitle:
								props.supporters.length > 0
									? "Última colaboración " +
									  (
											dayjs(
												props.supporters[0].createdAt
											) as any
									  ).fromNow()
									: "",
						})
					)}
					{props.challenge.type === "rose" ? (
						<Row style={{ marginTop: 16 }}>
							<Flower
								style={{
									marginRight: 12,
									minHeight: 24,
									minWidth: 24,
								}}
								height={24}
								width={24}
								color={ColorV2.text.neutralHard}
							/>
							<Text type="p" style={{ marginTop: 1 }}>
								Enviar una rosa tiene un coste de 5€
							</Text>
						</Row>
					) : (
						!completed &&
						!pastDate &&
						renderCell({
							icon: Calendar,
							title:
								dayjs(props.challenge.endDate.iso).diff(
									dayjs(),
									"days"
								) + " días",
							subtitle: "para completarlo",
						})
					)}
				</Col>
				{props.challenge.type === "dream" &&
					(appLocation
						? appLocation.currency.name.toUpperCase() === "EUR"
						: true) && (
						<Row
							style={{
								backgroundColor: ColorV2.surface.secondarySoft,
								padding: 16,
								borderRadius: 12,
								marginTop: 24,
							}}
						>
							<Text
								type="p2"
								style={{ color: ColorV2.text.neutralHard }}
							>
								<span
									style={{
										fontWeight: 600,
									}}
								>
									Recupera hasta el 80%
								</span>{" "}
								de tus donaciones en la Declaración de la Renta.{" "}
								<SpanLink
									onClick={() => setShowReliefModal(true)}
								>
									Saber más
								</SpanLink>
							</Text>
						</Row>
					)}
				<Col style={{ marginTop: 24, gap: 8 }}>
					<Button
						style={{
							backgroundColor:
								props.challenge.type === "rose"
									? ColorV2.surface.redMedium
									: ColorV2.surface.primary,
						}}
						onClick={props.onClick}
					>
						{props.challenge.type === "signature"
							? "Quiero firmar"
							: props.challenge.type === "rose"
							? "Quiero regalar una rosa"
							: props.challenge.type === "birthday"
							? "Enviar mi felicitación"
							: "Quiero colaborar"}
					</Button>
				</Col>
			</Section>
		</ResumeStyled>
	);
};
export default Resume;
export interface Props {
	challenge: ChallengeScheme;
	supporters: { user: UserScheme; createdAt: Date }[];
	letterCount?: number;
	onClick?: () => void;
	onShareClick: (op: { id: string }) => void;
}
